import { workspaceFetch } from '../../../../shared/platform/business-workspaces';
import { createLiveScenarioKnowledgeAdapter } from '../../../scenario-center/knowledge/adapters/live-scenario-knowledge-adapter';
import type {
  RegistryDocument,
  RequirementsDocument,
} from '../../../scenario-center/knowledge/consumer/contract';
import type { MatchingSource } from '../../../scenario-center/knowledge/consumer/exact-matching';
import { createLiveLocalAssetWorkflowAdapter } from '../../assets/adapters/live-local-asset-workflow-adapter';
import type {
  PublicationView,
  ServerOutcome,
} from '../../assets/adapters/local-asset-workflow-port';
import { createLiveCapabilityDefinitionAdapter } from '../../definition/adapters/live-capability-definition-adapter';
import { createSynAssessmentAdapter, type AssessmentAuth } from './syn-assessment-adapter';

function confirmed<T>(outcome: ServerOutcome<T>): T {
  if (outcome.kind !== 'OK')
    throw new Error(outcome.kind === 'REFUSED' ? outcome.code : outcome.kind);
  return outcome.value;
}
export function createSynExactMatchingReader(
  origin: string,
  auth: AssessmentAuth,
  send: typeof fetch = workspaceFetch,
) {
  // Reuse the strict S4 loopback guard before constructing any other readers.
  const assessment = createSynAssessmentAdapter(origin, auth, send);
  const knowledge = createLiveScenarioKnowledgeAdapter({ baseOrigin: origin, fetchImpl: send });
  const assets = createLiveLocalAssetWorkflowAdapter({
    baseOrigin: origin,
    fetchImpl: send,
    csrf: () => auth.csrf,
  });
  const definitions = createLiveCapabilityDefinitionAdapter({
    baseOrigin: origin,
    fetchImpl: send,
  });
  return {
    knowledge,
    assessment,
    async read(
      requirements: RequirementsDocument,
      registry: RegistryDocument,
    ): Promise<MatchingSource> {
      const observedAt = new Date().toISOString();
      let country = 'UNKNOWN',
        agency = 'UNKNOWN';
      try {
        const pin = requirements.scenarioTemplateExactPin;
        const response = await send(
          `${origin}/read-models/versioned-content/exact?${new URLSearchParams({ ...pin })}`,
          {
            credentials: 'omit',
            redirect: 'error',
            cache: 'no-store',
          },
        );
        if (!response.ok || !response.headers.get('Content-Type')?.includes('application/json'))
          throw new Error('SCENARIO_SOURCE_UNAVAILABLE');
        const exact = (await response.json()) as {
          status: string;
          query: Record<string, string>;
          snapshot: { pin: Record<string, string>; canonicalContent: string } | null;
        };
        if (
          exact.status !== 'VERIFIED' ||
          !exact.snapshot ||
          Object.entries(pin).some(([k, v]) => exact.query[k] !== v || exact.snapshot?.pin[k] !== v)
        )
          throw new Error('SCENARIO_EXACT_PIN_UNVERIFIED');
        const bytes = Uint8Array.from(atob(exact.snapshot.canonicalContent), (c) =>
          c.charCodeAt(0),
        );
        const digest = Array.from(
          new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)),
          (b) => b.toString(16).padStart(2, '0'),
        ).join('');
        if (digest !== pin.digest) throw new Error('SCENARIO_DIGEST_MISMATCH');
        // G123 remains the requirement Owner; S6 only verifies its declared exact identity.
        for (const row of requirements.requirements) {
          const ref = row.capabilityDefinitionExactRef;
          if (row.mappingState !== 'MAPPED' || !ref.capabilityId) continue;
          const d = await definitions.read({ capabilityId: ref.capabilityId });
          if (
            d.status !== 'AVAILABLE' ||
            d.snapshot?.definitionVersion !== ref.expectedDefinitionVersion ||
            d.snapshot.definitionDigest !== ref.expectedDefinitionDigest ||
            d.snapshot.definitionStatus !== 'PUBLISHED'
          )
            throw new Error('DEFINITION_EXACT_PIN_MISMATCH');
        }
        const scope = confirmed(await assets.scope({ session: auth.session }));
        country = scope.country;
        agency = scope.agency;
        const catalog = confirmed(await assets.catalog({ session: auth.session }));
        const publications: PublicationView[] = [];
        for (const asset of catalog) {
          if (asset.country !== country || asset.agency !== agency) throw new Error('SCOPE_DENIED');
          const p = [...asset.publications].sort(
            (a, b) => b.publishedVersion - a.publishedVersion,
          )[0];
          if (!p || p.assetId !== asset.assetId || p.country !== country || p.agency !== agency)
            throw new Error('ASSET_EXACT_PIN_MISMATCH');
          const exactAsset = confirmed(await assets.readExact({ ...p, session: auth.session }));
          if (
            exactAsset.status !== 'FOUND' ||
            !exactAsset.publication ||
            ['assetId', 'country', 'agency', 'publishedVersion', 'digest'].some(
              (k) =>
                exactAsset.publication?.[k as keyof PublicationView] !==
                p[k as keyof PublicationView],
            )
          )
            throw new Error('ASSET_EXACT_PIN_MISMATCH');
          publications.push(p);
        }
        const assessments = await assessment.list();
        if (assessments.some((a) => a.asset.country !== country || a.asset.agency !== agency))
          throw new Error('SCOPE_DENIED');
        const withdrawnRequirementIds = requirements.requirements
          .filter((r) =>
            r.sourceClaimRefs.some((id) => {
              const claim = registry.claimCandidates.claims.find((c) => c.claimId === id);
              const source = registry.sourceRegistry.sources.find(
                (s) => s.sourceId === claim?.sourceId,
              );
              return (
                !claim ||
                !source ||
                source.editionStatus.includes('WITHDRAWN') ||
                source.sourceType.includes('WITHDRAWN')
              );
            }),
          )
          .map((r) => r.requirementId);
        return {
          status: 'AVAILABLE',
          reason: 'OWNER_HTTP_EXACT_READ',
          observedAt,
          country,
          agency,
          publications,
          assessments,
          exactAssetsVerified: true,
          withdrawnRequirementIds,
        };
      } catch (error) {
        return {
          status: 'UNAVAILABLE',
          reason: error instanceof Error ? error.message : 'OWNER_UNAVAILABLE',
          observedAt,
          country,
          agency,
          publications: [],
          assessments: [],
          exactAssetsVerified: false,
        };
      }
    },
  };
}
